import { DelegationStatusEnum } from 'src/app/models/enums/delegation-status-enum';
import { DelegationService } from './../../data/delegation.service';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatStep, MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, throwError } from 'rxjs';
import { delay, finalize, first } from 'rxjs/operators';
import { ErrorCode } from 'src/app/common/error-codes/ErrorCode';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { DelegationBasicInfoFormGroupService } from '../services/delegation-basic-info-form-group.service';
import { DelegationSettlementDto } from 'src/app/models/dtos/delegation-settlement-dto';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { Permission } from 'src/app/common/enums';

const workerStatusesToEdit = [
  DelegationStatusEnum.Accepted,
  DelegationStatusEnum.ReturnedFromAccounting,
  DelegationStatusEnum.ReturnedFromSupervisor
]

@Component({
  selector: 'app-settle-delegation-form',
  templateUrl: './settle-delegation-form.component.html',
  styleUrls: ['./settle-delegation-form.component.scss'],
  standalone: false
})
export class SettleDelegationFormComponent implements OnInit, AfterViewInit, OnDestroy {
  private delegationService = inject(DelegationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private snackbarService = inject(SnackBarService);
  private spinner = inject(NgxSpinnerService);
  private delegationBasicInfoFormGroupService = inject(DelegationBasicInfoFormGroupService);
  private authService = inject(AuthService);

  delegationBasicInfoFormGroup: UntypedFormGroup;

  workerId: number;
  delegationId: number;
  delegationSettlement: DelegationSettlementDto;

  initialStep: number | null;
  initialTabName: string | null;

  @ViewChild('stepper', { static: false }) stepper: MatStepper;

  private readonly unsubscribe$ = new Subject<void>();

  constructor() {
    this.initialTabName = this.route.snapshot.queryParamMap.get('step');
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.delegationId = +params.get('delegationId');
    });

    this.delegationBasicInfoFormGroup = this.delegationBasicInfoFormGroupService.buildFormGroup(true);
  }

  ngAfterViewInit() {
    this.fetchDelegationData(this.delegationId)
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  isUserOwnDelegation = (): boolean => !!this.delegationSettlement && this.authService.authServerUserId === this.delegationSettlement.AuthServerUserId;

  areSettlementStepsVisible = (): boolean => !!this.delegationSettlement &&
    (this.delegationSettlement.DelegationStatusId === DelegationStatusEnum.PendingApprovalAccounting);

  canEdit(): boolean {
    if (this.delegationSettlement?.AuthServerUserId === this.authService.authServerUserId)
      return workerStatusesToEdit.includes(this.delegationSettlement.DelegationStatusId);

    if (this.authService.hasPermission(Permission.HRDelegationManagement) 
        || this.authService.isSupervisor(this.delegationSettlement?.AuthServerUserId)) {
      if (this.delegationSettlement?.DelegationStatusId !== DelegationStatusEnum.SentForSettlement &&
        this.delegationSettlement?.DelegationStatusId !== DelegationStatusEnum.PendingApprovalAccounting) {
        return true;
      }

      return false;
    }

    return this.delegationSettlement?.DelegationStatusId !== DelegationStatusEnum.SentForSettlement && 
    (this.authService.hasPermission(Permission.FullDelegationsManagement)
      || this.authService.hasPermission(Permission.AccountingDelegationManagement)
      || this.authService.hasPermission(Permission.AccountingDelegationManagementAllAccess));
  }

  isMileageStepAvailable = (): boolean => !!this.delegationSettlement && this.delegationSettlement.MileageSettlementAvailable;

  isDelegationAccepted = (): boolean => !!this.delegationSettlement && this.delegationSettlement.DelegationStatusId === DelegationStatusEnum.Accepted;

  fetchDelegationData(delegationId: number) {
    const success = (delegationSettlement: DelegationSettlementDto) => {
      this.delegationSettlement = delegationSettlement;
      this.fillFormGroups(delegationSettlement);
      setTimeout(() => this.moveStepperToInitialStep());
    };

    const error = () => {
      this.snackbarService.openErrorSnackBar(ErrorCode.SomethingWentWrong);
      this.router.navigate(['home']);
    };

    this.spinner.show();
    this.delegationService
      .getDelegationSettlementById(null, delegationId)
      .pipe(
        delay(0),
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe(
        (delegationSettlement) => {
          if (!delegationSettlement) throwError(ErrorCode.SomethingWentWrong);
          success(delegationSettlement);
        },
        () => error(),
      );
  }

  private fillFormGroups(delegationSettlement: DelegationSettlementDto) {
    this.delegationBasicInfoFormGroupService.setFormGroupValue(this.delegationBasicInfoFormGroup, delegationSettlement);
  }

  private moveStepperToInitialStep() {
    this.initialStep = this.mapTabToStepIndex(this.initialTabName);
    if (this.initialStep === null) {
      return;
    }
    this.setStepperSelectedIndex(this.stepper, this.initialStep);
  }

  private setStepperSelectedIndex(stepper: MatStepper, selectedIndex: number) {
    stepper.linear = false;
    stepper.selectedIndex = selectedIndex;
    stepper.linear = true;
  }

  isActiveStep(step: MatStep) {
    return this.stepper?.selectedIndex === this.stepper?.steps.toArray().indexOf(step);
  }

  private mapTabToStepIndex(tab: string | null): number | null {
    if (!tab) {
      return null;
    }
    
    const tabLower = tab.toLowerCase();
    const hasMileage = this.isMileageStepAvailable();
    const offset = hasMileage ? 0 : -1;
    
    switch (tabLower) {
      case 'costs':
        return 0;
      case 'mileages':
        return 1 + offset;
      case 'diets':
        return 2 + offset;
      case 'lumpsums':
        return 3 + offset;
      default:
        return null;
    }
  }
}
